// src/pages/bookings/Form.jsx
import { useState, useEffect, useRef, Fragment } from "react";
import {
  HStack,
  useRadioGroup,
  Box,
  useRadio,
  chakra,
  Button,
  Flex,
  FormLabel,
  FormControl,
  Input,
  Select,
  Checkbox,
  Grid,
  Text,
  FormErrorMessage,
  Tooltip,
} from "@chakra-ui/react";
import PropTypes from "prop-types";
import InputSuggestionWithExposeRef from "../../components/InputSuggestionWithExposeRef";
import PickupTime from "../../components/PickupTime";
import DateInput from "../../components/PickupDate";
import { isValidLocationString } from "../../utils/validationRules";
import moment from "moment-timezone";
import axios from "axios";
import { LuArrowUpDown } from "react-icons/lu";
import { RiSearchLine } from "react-icons/ri";
import { CgAddR } from "react-icons/cg";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import CurrentBookingCard from "./PackageCard";
import { useToast } from "@chakra-ui/react";

const FORM_WIDTH = { base: "100%", md: "500px" };

const toNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getResolvedLocalPackage = (hoursOptions, selectedHours, selectedKm) => {
  if (!Array.isArray(hoursOptions) || !hoursOptions.length) return null;

  const parsedHours = toNumberOrNull(selectedHours);
  const parsedKm = toNumberOrNull(selectedKm);

  if (parsedHours == null) return hoursOptions[0];

  const exactMatch = hoursOptions.find(
    (pkg) =>
      Number(pkg.hours) === parsedHours &&
      (parsedKm == null || Number(pkg.km) === parsedKm),
  );

  if (exactMatch) return exactMatch;

  const sameHourMatch = hoursOptions.find(
    (pkg) => Number(pkg.hours) === parsedHours,
  );

  return sameHourMatch || hoursOptions[0];
};

const Form = ({ data, setData, formRef }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [errors, setErrors] = useState({});
  const [isHovered, setIsHovered] = useState(false);
  const [availableHours, setAvailableHours] = useState([]);

  const fromRef = useRef();
  const toRef = useRef();

  /* ---------------- RADIO ---------------- */
  const CustomRadio = ({ name, radioProps }) => {
    const { state, getInputProps, htmlProps } = useRadio(radioProps);

    return (
      <chakra.label
        {...htmlProps}
        cursor="pointer"
        display="flex"
        alignItems="center"
        p={2}
      >
        <input {...getInputProps()} hidden />
        <Box
          w="24px"
          h="24px"
          mr={2}
          border="2px solid"
          borderColor={state.isChecked ? "#1814F3" : "#CBD5E0"}
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {state.isChecked && (
            <Box w="14px" h="14px" bg="#1814F3" borderRadius="full" />
          )}
        </Box>
        <Text fontWeight="500" color={state.isChecked ? "#1814F3" : "#000"}>
          {name}
        </Text>
      </chakra.label>
    );
  };

  const radioOptions = [
    { name: "Outstation oneway", value: "OW" },
    { name: "Round trip", value: "RT" },
    { name: "Local", value: "LR" },
    { name: "Airport", value: "AT" },
  ];

  const { getRadioProps, getRootProps } = useRadioGroup({
    name: "tripType",
    defaultValue: data?.tripType || "OW",
    onChange: (val) =>
      setData((p) => {
        if (val !== "LR") {
          return {
            ...p,
            tripType: val,
            hours: "",
            distance: "",
          };
        }

        const selectedPackage = getResolvedLocalPackage(
          availableHours,
          p.hours,
          p.distance,
        );

        return {
          ...p,
          tripType: val,
          hours: selectedPackage?.hours ?? p.hours ?? "",
          distance: selectedPackage?.km ?? p.distance ?? "",
        };
      }),
  });

  /* ---------------- HELPERS ---------------- */
  const swapFromAndTo = () => {
    const fromVal = fromRef.current?.getValue();
    const toVal = toRef.current?.getValue();
    fromRef.current?.setValue(toVal);
    toRef.current?.setValue(fromVal);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target ?? {};
    if (!name) return;
    setData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const addStop = () => {
    setData((p) => {
      if (p.stops && p.stops.length >= 5) {
        toast({
          title: "Stop limit reached",
          description: "You can only add up to 5 stops.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return p;
      }
      return { ...p, stops: [...(p.stops || []), ""] };
    });
  };
  const removeStop = (idx) => {
    setData((p) => {
      const newStops = p.stops.filter((_, i) => i !== idx);
      // Clear return date for oneway trips when all stops are removed
      const shouldClearReturnDate = p.tripType === "OW" && newStops.length === 0;
      return {
        ...p,
        stops: newStops,
        returndate: shouldClearReturnDate ? "" : p.returndate,
      };
    });
    setErrors((prev) => ({ ...prev, stops: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Get values from refs before navigation
    const fromValue = fromRef.current?.getValue();
    const toValue =
      data.tripType === "LR" ? "local" : toRef.current?.getValue();

    // Validation logic
    const newErrors = {};

    if (!fromValue) {
      newErrors.from = "Pickup location is required.";
    } else if (!isValidLocationString(fromValue)) {
      newErrors.from = "Please enter a valid pickup location (min 3 chars).";
    }

    // Only validate drop location for non-LR trips
    if (data.tripType !== "LR") {
      if (!toValue) {
        newErrors.to = "Drop location is required.";
      } else if (!isValidLocationString(toValue)) {
        newErrors.to = "Please enter a valid drop location (min 3 chars).";
      }
    }

    if (
  data.tripType !== "LR" &&
  fromValue &&
  toValue &&
  fromValue.trim().toLowerCase() === toValue.trim().toLowerCase()
) {
  newErrors.to = "Pickup and Drop location cannot be the same.";
}

    if (data.stops && data.stops.some((stop) => !stop || stop.trim() === "")) {
      newErrors.stops = "All stops must be filled or removed.";
    }

    if (!data.pickupdate) {
      newErrors.pickupdate = "Pickup date is required.";
    }

    if (!data.pickuptime) {
      newErrors.pickuptime = "Pickup time is required.";
    }

    if (data.tripType === "LR" && !data.hours) {
      newErrors.hours = "Please select local rental hours.";
    }

    if (data.tripType === "RT" && !data.returndate) {
      newErrors.returndate = "Return date is required for round trips.";
    }

    if (data.tripType === "OW" && data.stops && data.stops.length > 0 && !data.returndate) {
      newErrors.returndate = "End date is required when stops are added.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Process stops to extract address string from objects or use string values
    const processedStops = (data.stops || []).map((stop) => {
      if (typeof stop === "object" && stop !== null) {
        // Extract the full location data, not just address
        return stop;
      }
      return stop || "";
    });

    const submitData = {
      ...data,
      from: fromValue || "",
      to: toValue || "",
      hours:
        data.tripType === "LR"
          ? getResolvedLocalPackage(availableHours, data.hours, data.distance)
              ?.hours ||
            ""
          : data.hours || "",
      distance:
        data.tripType === "LR"
          ? getResolvedLocalPackage(availableHours, data.hours, data.distance)
              ?.km ||
            ""
          : data.distance || "",
      returndate: data.returndate || "",
      stops: processedStops,
    };

    navigate("/bookings/lead/results", { state: submitData });
  };

  useEffect(() => {
    setData((p) => ({
      ...p,
      tripType: p.tripType || "OW",
      pickupdate: p.pickupdate || "",
      pickuptime: p.pickuptime || "",
      returndate: p.returndate || "",
      hours: p.hours || "",
      distance: p.distance || "",
      stops: p.stops || [],
      airportTripType: p.airportTripType || (p.tripType === "AT" ? "PFA" : ""),
    }));
  }, []);

  // Set default airportTripType when trip type changes to AT
  useEffect(() => {
    if (data.tripType === "AT" && !data.airportTripType) {
      setData((p) => ({ ...p, airportTripType: "PFA" }));
    }
  }, [data.tripType]);

  useEffect(() => {
    const fetchLocalRentalHours = async () => {
      if (data.tripType !== "LR") return;

      try {
        const token = localStorage.getItem("prima_session");
        if (!token) return;

        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}/management/vehicle-segments?page=1&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const vehicles = res.data?.data || [];
        const hoursSet = new Set();

        vehicles.forEach((vehicle) => {
          const rentalOptions = vehicle.pricing?.localRental || [];
          rentalOptions.forEach((option) => {
            if (option.durationHours && option.includedKm && vehicle.status === "active") {
              hoursSet.add(
                JSON.stringify({
                  hours: option.durationHours,
                  km: option.includedKm,
                })
              );
            }
          });
        });

        const hoursArray = Array.from(hoursSet)
          .map((item) => JSON.parse(item))
          .sort((a, b) => a.hours - b.hours);

        setAvailableHours(hoursArray);

        if (hoursArray.length > 0) {
          setData((p) => {
            const selectedPackage = getResolvedLocalPackage(
              hoursArray,
              p.hours,
              p.distance,
            );

            return {
              ...p,
              hours: selectedPackage?.hours ?? "",
              distance: selectedPackage?.km ?? "",
            };
          });
        }
      } catch (error) {
        toast({
          title: "Unable to load local rental hours",
          description: error?.response?.data?.message || error.message,
          status: "warning",
          duration: 2500,
          isClosable: true,
        });
      }
    };

    fetchLocalRentalHours();
  }, [data.tripType]);

  return (
    <>
      <Flex flexDir={{ base: "column", md: "row" }} gap={2}>
        <Box px={6} py={8} bg="white" borderRadius="2xl" position="relative">
          <form onSubmit={handleSubmit} ref={formRef}>
            <Flex flexDir="column" gap={4}>
              {/* RADIOS */}
              <Box mb={4} {...getRootProps()}>
                <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap">
                  {radioOptions.map((o) => (
                    <CustomRadio
                      key={o.value}
                      name={o.name}
                      radioProps={getRadioProps({ value: o.value })}
                    />
                  ))}
                </HStack>
              </Box>

              {/* OUTSTATION */}
              {["OW", "RT"].includes(data.tripType) && (
                <>
                  <FormControl maxW={FORM_WIDTH} isInvalid={!!errors.from}>
                    <InputSuggestionWithExposeRef
                      ref={fromRef}
                      placeholder="Enter Start Location"
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        setData((p) => ({ ...p, from: value }));
                        setErrors((p) => ({ ...p, from: undefined }));
                      }}
                    />
                    {errors.from && (
                      <FormErrorMessage>{errors.from}</FormErrorMessage>
                    )}
                  </FormControl>

                  <Button
                    type="button"
                    position="absolute"
                    right={28}
                    top={36}
                    border="2px solid #1814F3"
                    borderRadius="full"
                    bg="white"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={swapFromAndTo}
                  >
                    <LuArrowUpDown color={isHovered ? "white" : "#1814F3"} />
                  </Button>

                  <FormControl maxW={FORM_WIDTH} isInvalid={!!errors.to}>
                    <InputSuggestionWithExposeRef
                      ref={toRef}
                      placeholder="Enter To Location"
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        setData((p) => ({ ...p, to: value }));
                        setErrors((p) => ({ ...p, to: undefined }));
                      }}
                    />
                    {errors.to && (
                      <FormErrorMessage>{errors.to}</FormErrorMessage>
                    )}
                  </FormControl>

                  {(!data.stops || data.stops.length < 5) && (
                    <Flex
                      onClick={addStop}
                      cursor="pointer"
                      color="#1814F3"
                      align="center"
                      width="15%"
                    >
                      <CgAddR />
                      <Text ml={1}>Add Stops</Text>
                    </Flex>
                  )}

                  {errors.stops && (
                    <Text color="red.500" fontSize="xs" mb={2}>
                      {errors.stops}
                    </Text>
                  )}

                  {(data.stops && data.stops.length > 0 || (data.tripType === "RT" && data.from && data.to)) && (
                    <Box w="100%">
                      <Box
                        bg="blue.50"
                        borderRadius="md"
                        p={3}
                        borderLeft="3px solid"
                        borderColor="blue.500"
                        mb={3}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="blue.700"
                          mb={1}
                        >
                          Trip Route:
                        </Text>
                        <Text fontSize="sm" color="blue.800" fontWeight="500">
                          {(() => {
                            const getLocationString = (loc) => {
                              if (typeof loc === "string") return loc;
                              return (
                                loc?.name ||
                                loc?.description ||
                                loc?.address ||
                                ""
                              );
                            };

                            const truncate = (text) => {
                              if (!text) return "";
                              const firstCommaIndex = text.indexOf(",");
                              const firstWhitespaceIndex = text.indexOf(" ");

                              if (firstCommaIndex !== -1) {
                                return text.slice(0, firstCommaIndex);
                              }

                              return firstWhitespaceIndex !== -1
                                ? text.slice(0, firstWhitespaceIndex)
                                : text;
                            };

                            const start =
                              getLocationString(data.from) || "Start";
                            const end =
                              getLocationString(data.to) || "Destination";
                            const hasStart = !!getLocationString(data.from);
                            const hasEnd = !!getLocationString(data.to);

                            const stopNames = (data.stops || [])
                              .map((stop) => getLocationString(stop))
                              .filter((stop) => stop && stop.trim());

                            const segments = [start, ...stopNames, end];

                            if (data.tripType === "RT" && hasStart && hasEnd) {
                              segments.push(start);
                            }

                            return segments.map((segment, index) => (
                              <Fragment key={`${segment}-${index}`}>
                                {index > 0 && (
                                  <Text as="span" color="blue.600">
                                    {" → "}
                                  </Text>
                                )}
                                <Tooltip
                                  label={segment}
                                  hasArrow
                                  placement="top"
                                >
                                  <Text as="span" cursor="help">
                                    {truncate(segment)}
                                  </Text>
                                </Tooltip>
                              </Fragment>
                            ));
                          })()}
                        </Text>
                      </Box>
                      {data.stops.map((stop, idx) => (
                        <Flex key={idx} mb={3} align="flex-end" gap={2}>
                          <FormControl maxW={FORM_WIDTH}>
                            <FormLabel fontSize="sm">Stop {idx + 1}</FormLabel>
                            <InputSuggestionWithExposeRef
                              placeholder={`Enter Stop ${idx + 1}`}
                              value={
                                typeof stop === "object" && stop !== null
                                  ? stop.address || stop.name || ""
                                  : stop || ""
                              }
                              onChange={(e) => {
                                const newStops = [...data.stops];
                                // Extract the address string from the event object
                                const value = e.target?.value || e;
                                newStops[idx] = value;
                                setData((p) => ({ ...p, stops: newStops }));
                                setErrors((prev) => ({ ...prev, stops: undefined }));
                              }}
                              filter="address"
                              country="in"
                            />
                          </FormControl>
                          <Button
                            type="button"
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeStop(idx)}
                            title="Remove Stop"
                          >
                            <FiTrash2 />
                          </Button>
                        </Flex>
                      ))}
                    </Box>
                  )}

                  <Grid templateColumns="repeat(4,1fr)" gap={4}>
                    <Box>
                      <DateInput
                        label="Pickup Date"
                        name="pickupdate"
                        value={data.pickupdate}
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickupdate && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickupdate}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <PickupTime
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickuptime && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickuptime}
                        </Text>
                      )}
                    </Box>
                    {(data.tripType === "RT" || (data.tripType === "OW" && data.stops && data.stops.length > 0)) && (
                      <Box>
                        <DateInput
                          label={data.tripType === "RT" ? "Return Date" : "End Date"}
                          name="returndate"
                          minDate={
                            data.pickupdate
                              ? data.pickupdate
                              : new Date().toISOString().split("T")[0]
                          }
                          value={data.returndate}
                          handleInputChange={handleInputChange}
                        />
                        {errors.returndate && (
                          <Text color="red.500" fontSize="xs">
                            {errors.returndate}
                          </Text>
                        )}
                      </Box>
                    )}
                    <Box />
                  </Grid>
                </>
              )}

              {/* AIRPORT */}
              {data.tripType === "AT" && (
                <>
                  <FormControl maxW={FORM_WIDTH}>
                    <FormLabel>Trip</FormLabel>
                    <Select
                      name="airportTripType"
                      value={data.airportTripType}
                      onChange={handleInputChange}
                    >
                      <option value="PFA">Pickup From Airport</option>
                      <option value="DTA">Drop At Airport</option>
                    </Select>
                  </FormControl>

                  <FormControl maxW={FORM_WIDTH} isInvalid={!!errors.from}>
                    <InputSuggestionWithExposeRef
                      ref={fromRef}
                      name="from"
                      placeholder="From Location"
                      fromOrTo={data.airportTripType === "PFA" ? "from" : null}
                      filter={
                        data.airportTripType === "PFA"
                          ? "airport_with_terminal"
                          : "address"
                      }
                      airportTripType={data.airportTripType}
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        setData((p) => ({ ...p, from: value }));
                        setErrors((p) => ({ ...p, from: undefined }));
                      }}
                    />
                    {errors.from && (
                      <Text color="red.500" fontSize="xs">
                        {errors.from}
                      </Text>
                    )}
                  </FormControl>

                  <FormControl maxW={FORM_WIDTH} isInvalid={!!errors.to}>
                    <InputSuggestionWithExposeRef
                      ref={toRef}
                      name="to"
                      placeholder="To Location"
                      fromOrTo={data.airportTripType === "DTA" ? "to" : null}
                      filter={
                        data.airportTripType === "PFA" ? "address" : "airport"
                      }
                      airportTripType={data.airportTripType}
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        setData((p) => ({ ...p, to: value }));
                        setErrors((p) => ({ ...p, to: undefined }));
                      }}
                    />
                    {errors.to && (
                      <Text color="red.500" fontSize="xs">
                        {errors.to}
                      </Text>
                    )}
                  </FormControl>

                  <Grid templateColumns="repeat(4,1fr)" gap={4}>
                    <Box>
                      <DateInput
                        value={data.pickupdate}
                        label="Pickup Date"
                        name="pickupdate"
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickupdate && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickupdate}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <PickupTime
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickuptime && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickuptime}
                        </Text>
                      )}
                    </Box>
                    <Box />
                    <Box />
                  </Grid>
                </>
              )}

              {/* LOCAL */}
              {data.tripType === "LR" && (
                <>
                  <FormControl isInvalid={!!errors.hours}>
                    <FormLabel>Hours</FormLabel>
                    <Flex
                      border="1px solid"
                      borderColor="blue.400"
                      borderRadius="md"
                      overflow="hidden"
                      w="fit-content"
                      flexWrap="wrap"
                    >
                      {availableHours.length > 0 ? (
                        availableHours.map((option, index) => {
                          const isSelected = Number(data.hours) === Number(option.hours) && Number(data.distance) === Number(option.km);

                          return (
                            <Flex
                              key={`${option.hours}-${option.km}`}
                              px={4}
                              py={2}
                              align="center"
                              justify="center"
                              cursor="pointer"
                              bg={isSelected ? "blue.500" : "white"}
                              color={isSelected ? "white" : "gray.700"}
                              fontSize="sm"
                              fontWeight="500"
                              borderRight={
                                index !== availableHours.length - 1
                                  ? "1px solid"
                                  : "none"
                              }
                              borderColor="blue.600"
                              _hover={{
                                bg: isSelected ? "blue.600" : "blue.50",
                              }}
                              onClick={() => {
                                setData((p) => ({ ...p, hours: option.hours, distance: option.km }));
                                setErrors((p) => ({ ...p, hours: undefined }));
                              }}
                            >
                              {option.hours} hrs | {option.km} km
                            </Flex>
                          );
                        })
                      ) : (
                        <Box px={4} py={2}>
                          <Text fontSize="sm" color="gray.500">
                            No local rental packages available
                          </Text>
                        </Box>
                      )}
                    </Flex>
                    {errors.hours && (
                      <FormErrorMessage>{errors.hours}</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl maxW={FORM_WIDTH} isInvalid={!!errors.from}>
                    <InputSuggestionWithExposeRef
                      ref={fromRef}
                      placeholder="Pickup Location"
                      name="from"
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        setData((p) => ({ ...p, from: value }));
                        setErrors((p) => ({ ...p, from: undefined }));
                      }}
                    />
                    {errors.from && (
                      <Text color="red.500" fontSize="xs">
                        {errors.from}
                      </Text>
                    )}
                  </FormControl>

                  <Grid templateColumns="repeat(4,1fr)" gap={4}>
                    <Box>
                      <DateInput
                        value={data.pickupdate}
                        label="Pickup Date"
                        name="pickupdate"
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickupdate && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickupdate}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <PickupTime
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                      {errors.pickuptime && (
                        <Text color="red.500" fontSize="xs">
                          {errors.pickuptime}
                        </Text>
                      )}
                    </Box>
                    <Box />
                    <Box />
                  </Grid>
                </>
              )}

              {/* SUBMIT */}
              <Button type="submit" w="150px">
                <RiSearchLine />
                <Text ml={2}>Search</Text>
              </Button>
            </Flex>
          </form>
        </Box>
      </Flex>

      {["OW", "RT"].includes(data.tripType) && (
        <Box mt={8}>
          <CurrentBookingCard
            fromLocation={data.from}
            toLocation={data.to}
            pickupDate={data.pickupdate}
            pickupTime={data.pickuptime}
            returnDate={data.returndate}
            tripType={data.tripType}
          />
        </Box>
      )}
    </>
  );
};

Form.propTypes = {
  data: PropTypes.object.isRequired,
  setData: PropTypes.func.isRequired,
  formRef: PropTypes.object,
};

export default Form;
